import { PageTitle as SharedPageTitle } from '../../../components/layout/PageHeader';
import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useComplianceDocuments } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import type { ComplianceDocument } from '../../../types/api';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  buildComplianceListParams,
  DEFAULT_COMPLIANCE_FILTERS,
  type ComplianceFiltersState,
} from '../lib/filters';
import { formatComplianceTypeLabel } from '../lib/labels';
import { ComplianceFilters } from './ComplianceFilters';
import { CompliancePagination } from './CompliancePagination';
import { ComplianceTable } from './ComplianceTable';
import { isSafeApiDownloadUrl } from '../../user-files/lib/downloadUrl';

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0;
  margin: 0;
`;

const Header = styled.div`
  margin-bottom: var(--space-xl);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: var(--space-sm);
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const StatusNote = styled.p`
  margin: var(--space-sm) 0 0 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: var(--space-3xl);
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-lg);
  opacity: 0.6;
`;

const EmptyMessage = styled.p`
  margin: 0 0 var(--space-md) 0;
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
`;

const ErrorState = styled(Card)`
  text-align: center;
  padding: var(--space-3xl);
  color: var(--danger-600);
`;

export const ComplianceListView: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState<ComplianceFiltersState>(
    DEFAULT_COMPLIANCE_FILTERS,
  );
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

  const debouncedSearch = useDebounce(filters.search, 300);
  const debouncedTags = useDebounce(filters.tags, 300);

  const listParams = React.useMemo(
    () =>
      buildComplianceListParams({
        ...filters,
        search: debouncedSearch,
        tags: debouncedTags,
      }),
    [filters, debouncedSearch, debouncedTags],
  );

  const { data, isLoading, error, refetch, isFetching } =
    useComplianceDocuments(listParams);
  const documents = React.useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;

  const typeOptions = React.useMemo(() => {
    const types = new Set<string>(documents.map((document) => document.type));
    if (filters.type) {
      types.add(filters.type);
    }
    return Array.from(types)
      .sort()
      .map((type) => ({
        value: type,
        label: formatComplianceTypeLabel(type),
      }));
  }, [documents, filters.type]);

  const hasActiveFilters =
    Boolean(filters.status) ||
    Boolean(filters.type) ||
    Boolean(filters.expiryWindow) ||
    Boolean(filters.search.trim()) ||
    Boolean(filters.tags.trim());

  const handleFiltersChange = React.useCallback(
    (updates: Partial<ComplianceFiltersState>) => {
      setFilters((prev) => ({
        ...prev,
        ...updates,
        page: updates.page ?? 1,
      }));
    },
    [],
  );

  const handleClearFilters = React.useCallback(() => {
    setFilters(DEFAULT_COMPLIANCE_FILTERS);
  }, []);

  const handleDownload = React.useCallback(
    async (document: ComplianceDocument) => {
      if (!document.storage_key) {
        toast.error('El documento no tiene archivo adjunto.');
        return;
      }

      setDownloadingId(document.id);

      try {
        const response = await apiService.downloadComplianceDocument(
          document.id,
        );
        if (!isSafeApiDownloadUrl(response.presigned_get_url)) {
          throw new Error('El enlace de descarga no es seguro.');
        }
        window.open(
          response.presigned_get_url,
          '_blank',
          'noopener,noreferrer',
        );
      } catch (downloadError) {
        const message =
          downloadError instanceof Error
            ? downloadError.message
            : 'No se pudo descargar el documento.';
        toast.error(message);
      } finally {
        setDownloadingId(null);
      }
    },
    [],
  );

  return (
    <PageContainer>
      <Header>
        <HeaderRow>
          <div>
            <Title>Cumplimiento</Title>
            <Subtitle>
              Gestiona los documentos regulatorios y sus fechas de vencimiento.
            </Subtitle>
          </div>
          <HeaderActions>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/compliance/new')}
            >
              Nuevo documento
            </Button>
          </HeaderActions>
        </HeaderRow>
        {isFetching && !isLoading && (
          <StatusNote role="status" aria-live="polite">
            Actualizando resultados…
          </StatusNote>
        )}
      </Header>

      <ComplianceFilters
        filters={filters}
        typeOptions={typeOptions}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        showClear={hasActiveFilters}
      />

      {isLoading ? (
        <Card>
          <LoadingSpinner message="Cargando documentos de cumplimiento..." />
        </Card>
      ) : error ? (
        <ErrorState>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyMessage>Error al cargar los documentos</EmptyMessage>
          <Button variant="primary" onClick={() => refetch()}>
            Reintentar
          </Button>
        </ErrorState>
      ) : documents.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📄</EmptyIcon>
          <EmptyMessage>
            {hasActiveFilters
              ? 'No se encontraron documentos con los filtros aplicados.'
              : 'Aún no hay documentos de cumplimiento registrados.'}
          </EmptyMessage>
          {!hasActiveFilters && (
            <p style={{ margin: 0 }}>
              Los documentos requeridos dependen de tu jurisdicción y se
              mostrarán aquí cuando se carguen.
            </p>
          )}
          {hasActiveFilters && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleClearFilters}
              style={{ marginTop: 'var(--space-md)' }}
            >
              Limpiar filtros
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <ComplianceTable
            documents={documents}
            loading={isLoading}
            onView={(document) => navigate(`/compliance/${document.id}`)}
            onDownload={handleDownload}
            downloadingId={downloadingId}
          />
          <CompliancePagination
            page={filters.page}
            perPage={filters.perPage}
            total={total}
            onPageChange={(page) => handleFiltersChange({ page })}
          />
        </>
      )}
    </PageContainer>
  );
};

ComplianceListView.displayName = 'ComplianceListView';
